import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FeatureFlagsService } from './feature-flags.service';
import { combineLatest, filter, Observable, Subject, takeUntil, map } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { OpenReplayService } from './open-replay.service';
import { ClarityService } from './clarity.service';
import { NGXLogger } from 'ngx-logger';
import { environment } from '../environments/environment';
import { MetaDataService } from './metadata.service';

@Component({
  selector: 'tea-shop-fe-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'tea-shop-fe';

  showStore$!: Observable<boolean>;
  isLoading = true;
  maintenanceMode$!: Observable<boolean>;
  comingSoon$!: Observable<boolean>;
  securing$!: Observable<boolean>;
  private destroy$ = new Subject<void>();
  private isNavigating = false;

  constructor(
    private featureFlagsService: FeatureFlagsService,
    private router: Router,
    private OpenReplayService: OpenReplayService,
    private clarityService: ClarityService,
    private logger: NGXLogger,
    private metadataService: MetaDataService,
  ) { }

  ngAfterViewInit() {
    this.logger.log('AppComponent:ngAfterViewInit');
    if (environment.OPTIMASATION_OPEN_REPLAY) {
      this.OpenReplayService.start();
    } else {
      this.logger.warn('OpenReplay disabled');
    }
    if (environment.OPTIMISATION_CLARITY) {
      this.clarityService.start();
    } else {
      this.logger.warn('Clarity disabled');
    }
  }

  ngOnInit() {
    this.logger.log('AppComponent');
    this.maintenanceMode$ = this.featureFlagsService.getBooleanValue$('maintenance-mode', false);
    this.comingSoon$ = this.featureFlagsService.getBooleanValue$('coming-soon', false);

    this.showStore$ = combineLatest([
      this.maintenanceMode$,
      this.comingSoon$
    ]).pipe(
      map(([maintenance, coming]) => !maintenance && !coming)
    );

    this.securing$ = combineLatest([
      this.maintenanceMode$,
      this.comingSoon$,
      this.showStore$
    ]).pipe(
      map(([maintenance, coming, store]) => !maintenance && !coming && !store)
    );

    // Subscribe to both navigation events and feature flag changes
    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );

    navigationEnd$.subscribe((event) => {
      const canonicalURL = `https://sencha-tea.com${event.urlAfterRedirects}`;
      this.metadataService.updateMetadata({url: canonicalURL});
      this.metadataService.setCanonicalLink(canonicalURL);
      this.logger.debug('Canonical URL set to:', canonicalURL);
    });


    // Combine latest values from feature flags and navigation
    combineLatest([
      navigationEnd$,
      this.featureFlagsService.getBooleanValue$('maintenance-mode', false),
      this.featureFlagsService.getBooleanValue$('coming-soon', false)
    ])
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe(([event, maintenanceMode, comingSoon]) => {
      if (!this.isNavigating) {
        this.isNavigating = true;
        this.handleNavigation(maintenanceMode, comingSoon);
        setTimeout(() => {
          this.isNavigating = false;
        }, 100);
      }
    });

    // Doesn't use the logger service, this is meant to be for the console
    console.log(
      '%c🔒 Security Researcher? We have a VDP! 🔒\n' +
      '%cVisit /vulnerability-disclosure-program',
      'color: #166534; font-size: 20px; font-weight: bold;',
      'color: #166534; font-size: 16px;'
    );
  }

  private handleNavigation(maintenanceMode: boolean, comingSoon: boolean) {
    const currentUrl = this.router.url;
    if (maintenanceMode && !currentUrl.includes('/maintenance')) {
      this.router.navigate(['/maintenance'], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    } else if (comingSoon && !currentUrl.includes('/coming-soon')) {
      this.router.navigate(['/coming-soon'], {
        queryParams: {
          returnUrl: currentUrl
        }
      });
    } else if (!maintenanceMode && !comingSoon && 
              (currentUrl.includes('/maintenance') || currentUrl.includes('/coming-soon'))) {
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl') || '/';
      this.router.navigateByUrl(returnUrl);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
